import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

export interface CreateMappingPrefillValues {
  type?: string
  source_id?: string
  source_system?: string
  display_name?: string
}

export interface UseCreateMappingDrawerResult {
  isOpen: boolean
  openDrawer: () => void
  closeDrawer: () => void
  prefillValues: CreateMappingPrefillValues | null
}

/**
 * Hook to manage Create Mapping Drawer state with URL param support.
 *
 * When navigating with `action=create_mapping` URL param, the drawer
 * auto-opens with prefilled values from the URL. Params are cleared
 * after consumption to prevent re-triggering on refresh.
 */
export function useCreateMappingDrawer(): UseCreateMappingDrawerResult {
  const [params, setSearchParams] = useSearchParams()
  const [isOpen, setIsOpen] = useState(false)
  const [prefillValues, setPrefillValues] =
    useState<CreateMappingPrefillValues | null>(null)

  const openDrawer = useCallback(() => {
    setIsOpen(true)
  }, [])

  const closeDrawer = useCallback(() => {
    setIsOpen(false)
    setPrefillValues(null)
  }, [])

  // Handle URL param-triggered drawer open
  useEffect(() => {
    const action = params.get('action')
    const prefillType = params.get('prefill_type')
    const prefillSourceId = params.get('prefill_source_id')
    const prefillSourceSystem = params.get('prefill_source_system')
    const prefillSourceName = params.get('prefill_source_name')

    if (action === 'create_mapping') {
      // Build prefill values from URL params
      const prefill: CreateMappingPrefillValues = {}
      if (prefillType) {
        prefill.type = prefillType
      }
      if (prefillSourceId) {
        prefill.source_id = prefillSourceId
      }
      if (prefillSourceSystem) {
        prefill.source_system = prefillSourceSystem
      }
      if (prefillSourceName) {
        prefill.display_name = prefillSourceName
      }

      setPrefillValues(Object.keys(prefill).length > 0 ? prefill : null)
      setIsOpen(true)

      // Clear action and prefill params after consumption to prevent re-triggering on refresh
      const newParams = new URLSearchParams(params.toString())
      newParams.delete('action')
      newParams.delete('prefill_type')
      newParams.delete('prefill_source_id')
      newParams.delete('prefill_source_system')
      newParams.delete('prefill_source_name')
      setSearchParams(newParams, { replace: true })
    }
  }, [params, setSearchParams])

  return {
    isOpen,
    openDrawer,
    closeDrawer,
    prefillValues,
  }
}
